import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';
import type { CatalogPage } from '../types/catalog-v2.types';
import type {
  IssueListing,
  LineageItem,
  LineageListing,
  RunLineageIssueGroups,
  RunLineageListingsQuery,
  RunLineageQuery,
  RunLineageSummary,
  Stage,
} from '../types/lineage-images.api.types';
import type { LineageJoinTarget, LineageStageJoin } from '../types/lineage-images.types';
import { type FallbackResult, readWithFallback } from '../utils/catalog-capabilities';
import { toPage, toSearchParams, unwrap } from '../utils/envelope';
import { ISSUE_PAGE_SIZE, type LineageIssueWalk } from '../utils/lineage-issues';
import { MAX_EXTRA_STAGE_PAGES, resolveTargets } from '../utils/lineage-join';

/** A run's lineage rows (one per product × stage × scope × attempt), keyset-paged in flow order. */
export async function getRunLineage(
  runId: string,
  query: RunLineageQuery,
): Promise<CatalogPage<LineageItem>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.lineage(runId), { searchParams: toSearchParams(query) })
    .json<CatalogListEnvelope<LineageItem>>();
  return toPage(body, (row) => row);
}

/** A page of a run's listings, each with every stage's newest attempt (§49), keyset-paged. */
export async function getRunLineageListings(
  runId: string,
  query: RunLineageListingsQuery,
): Promise<CatalogPage<LineageListing>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.lineageListings(runId), {
      searchParams: toSearchParams(query),
    })
    .json<CatalogListEnvelope<LineageListing>>();
  return toPage(body, (row) => row);
}

/** The lineage grid's page: §49 listings, or (older backend) one stage's flat lineage page. */
export type RunLineageGridPage = FallbackResult<
  CatalogPage<LineageListing>,
  CatalogPage<LineageItem>
>;

/**
 * One page of the lineage grid: `…/lineage/listings` when the backend has it, else the flat
 * `…/lineage` page of the paged stage, which the caller completes with per-stage joins.
 */
export function getRunLineageGridPage(
  runId: string,
  listingsQuery: RunLineageListingsQuery,
  fallbackQuery: RunLineageQuery,
): Promise<RunLineageGridPage> {
  return readWithFallback({
    capability: 'lineageListings',
    read: () => getRunLineageListings(runId, listingsQuery),
    fallback: () => getRunLineage(runId, fallbackQuery),
  });
}

/** `GET …/lineage/issues` (§49): the run's newest-attempt issues grouped by (stage, reason). */
export async function getRunLineageIssueGroups(
  runId: string,
  samplePerGroup: number,
): Promise<RunLineageIssueGroups> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.lineageIssues(runId), {
      searchParams: toSearchParams({ samplePerGroup }),
    })
    .json<CatalogEnvelope<RunLineageIssueGroups>>();
  return unwrap(body);
}

/** One issue group's listings after `after` (a group's `nextCursor`), keyset-paged (§49). */
export async function getRunLineageIssueListings(
  runId: string,
  query: { stage: Stage; reason: string; after: string | null; size: number },
): Promise<CatalogPage<IssueListing>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.lineageIssueListings(runId), {
      searchParams: toSearchParams(query),
    })
    .json<CatalogListEnvelope<IssueListing>>();
  return toPage(body, (row) => row);
}

/**
 * Fallback for backends without §49 `…/lineage/listings`: one stage's outcomes for a page of listings. The API has no product filter, so this reads the
 * stage page aligned with the grid's page (same index and size), then walks to the next / previous
 * page while a listing's item may sit there — at most `MAX_EXTRA_STAGE_PAGES` more reads.
 */
export async function getRunLineageStageJoin(
  runId: string,
  params: { stage: Stage; page: number; size: number; targets: readonly LineageJoinTarget[] },
): Promise<LineageStageJoin> {
  const { stage, size, targets } = params;
  const read = (page: number) => getRunLineage(runId, { stage, page, size });
  let first = await read(params.page);
  let lo = params.page;
  let extra = 0;
  // A stage with fewer pages than the grid's page index: start from its last page instead.
  if (first.items.length === 0 && params.page > first.totalPages && first.totalPages > 0) {
    lo = first.totalPages;
    first = await read(lo);
    extra += 1;
  }
  const totalPages = Math.max(first.totalPages, 1);
  let hi = lo;
  const pages = [first.items];
  for (;;) {
    const rows = pages.flat();
    const { join, needBefore, needAfter } = resolveTargets(
      rows,
      targets,
      lo === 1,
      hi >= totalPages,
    );
    if (extra >= MAX_EXTRA_STAGE_PAGES) return join;
    if (needAfter && hi < totalPages) {
      hi += 1;
      pages.push((await read(hi)).items);
    } else if (needBefore && lo > 1) {
      lo -= 1;
      pages.unshift((await read(lo)).items);
    } else {
      return join;
    }
    extra += 1;
  }
}

/** Rows a capped walk read, and whether rows were left unread when it stopped. */
export interface CappedRead<T> {
  items: T[];
  truncated: boolean;
}

/**
 * Follows `after` cursors from the first page until `limit` rows are read or the list ends —
 * one read per page of `pageSize`, never more than `ceil(limit / pageSize)`.
 */
async function walkPages<T>(
  read: (after: string | null, size: number) => Promise<CatalogPage<T>>,
  limit: number,
  pageSize: number,
): Promise<CappedRead<T>> {
  const items: T[] = [];
  let after: string | null = null;
  while (items.length < limit) {
    const page: CatalogPage<T> = await read(after, Math.min(pageSize, limit - items.length));
    items.push(...page.items);
    if (!page.nextCursor || page.items.length === 0) return { items, truncated: false };
    after = page.nextCursor;
  }
  return { items, truncated: true };
}

/**
 * Fallback for backends without §49 `…/lineage/issues`: a run's issue rows (failed / still
 * flagged / skipped) for the issues-by-reason view. The old API has no group-by and no product filter, so each planned (stage, status) is walked in pages of
 * `ISSUE_PAGE_SIZE`, in parallel, each stopping at its share of the row cap.
 */
export async function getRunLineageIssues(
  runId: string,
  walks: readonly LineageIssueWalk[],
): Promise<CappedRead<LineageItem>> {
  const reads = await Promise.all(
    walks.map(({ stage, status, limit }) =>
      walkPages(
        (after, size) => getRunLineage(runId, { stage, status, after, size }),
        limit,
        ISSUE_PAGE_SIZE,
      ),
    ),
  );
  return {
    items: reads.flatMap((read) => read.items),
    truncated: reads.some((read) => read.truncated),
  };
}

/** A run's lineage counted: outcomes per stage and IMPORT dispositions. */
export async function getRunLineageSummary(runId: string): Promise<RunLineageSummary> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.lineageSummary(runId))
    .json<CatalogEnvelope<RunLineageSummary>>();
  return unwrap(body);
}
